import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import {
  Print,
  Add as AddIcon,
  MoreVert,
  Close
} from '@mui/icons-material';
import { customerService } from '../services/customerService';
import '../styles/print.css';
import PrintPage from '../components/print/PrintPage';
import ResizableHeaderCell from '../components/ResizableHeaderCell';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { printWithFilename } from '../utils/printUtils';

const Customers = () => {
  const DEFAULT_COL_WIDTHS = {
    custNo: 120, shortName: 130, name: 220, phone: 140, city: 140, state: 90, country: 110,
    salesRep: 130, notes: 220, terms: 110, workCode: 110, active: 90, actions: 120
  };
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('customer_number');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { colWidths, startResize } = useResizableColumns({
    defaultWidths: DEFAULT_COL_WIDTHS,
    storageKey: 'wm_customers_col_widths',
    minWidths: {
      custNo: 90,
      shortName: 80,
      name: 140,
      phone: 110,
      city: 100,
      state: 70,
      country: 90,
      salesRep: 100,
      notes: 140,
      terms: 90,
      workCode: 90,
      active: 80,
      actions: 100
    }
  });

  const customerLongName = (c) =>
    c.company_name ||
    (c.first_name && c.last_name ? `${c.first_name} ${c.last_name}` : c.first_name || c.last_name || '');

  const customerShortName = (c) => {
    if (c?.short_name == null || String(c.short_name).trim() === '') return '';
    return String(c.short_name).trim();
  };

  /** Display / search / dialogs: long name, with short prefix when useful outside the table. */
  const custName = (c) => {
    const long = customerLongName(c);
    const sh = customerShortName(c);
    if (sh && long) return `${sh} · ${long}`;
    return sh || long || '-';
  };

  const searchableString = (c) => {
    const name = custName(c);
    return [
      c.customer_number,
      c.id,
      c.short_name,
      name,
      c.phone,
      c.email,
      c.city,
      c.state,
      c.country,
      c.salesman_id,
      c.sales_id,
      c.notes,
      c.terms,
      c.work_code,
      c.is_active ? 'active' : 'inactive'
    ].map((x) => String(x ?? '')).join(' ').toLowerCase();
  };

  const filteredAndSortedCustomers = useMemo(() => {
    let list = customers;
    if (statusFilter === 'active') list = list.filter((c) => c.is_active);
    else if (statusFilter === 'inactive') list = list.filter((c) => !c.is_active);
    const term = searchInput.trim().toLowerCase();
    if (term) list = list.filter((c) => searchableString(c).includes(term));
    const nameStr = (c) => customerLongName(c) || '-';
    const cmp = (a, b) => {
      let va = a[sortBy];
      let vb = b[sortBy];
      if (sortBy === 'name') {
        va = nameStr(a);
        vb = nameStr(b);
      } else if (sortBy === 'short_name') {
        va = customerShortName(a).toLowerCase() || '\uffff';
        vb = customerShortName(b).toLowerCase() || '\uffff';
      } else if (sortBy === 'is_active') {
        va = a.is_active ? 1 : 0;
        vb = b.is_active ? 1 : 0;
      } else {
        va = String(va ?? '').toLowerCase();
        vb = String(vb ?? '').toLowerCase();
      }
      if (va < vb) return sortOrder === 'asc' ? -1 : 1;
      if (va > vb) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    };
    return [...list].sort(cmp);
  }, [customers, statusFilter, searchInput, sortBy, sortOrder]);

  const handleSort = (column) => {
    if (sortBy === column) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  /** Match Orders / Quotes lists — tighter than MUI `size="small"` alone. */
  const customersTableDenseSx = {
    '& .MuiTableCell-root': {
      py: 0.35,
      px: '8px',
      fontSize: '0.75rem',
      lineHeight: 1.2,
      verticalAlign: 'middle'
    },
    '& .MuiTableCell-head': {
      py: 0.45,
      fontSize: '0.7rem'
    },
    '& .MuiTableCell-head .MuiBox-root': { pr: 1 },
    '& .MuiChip-root': {
      height: 20,
      maxWidth: '100%',
      '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem', lineHeight: 1.1 }
    },
    '& .MuiIconButton-root': { p: 0.25 },
    '& .MuiButton-sizeSmall': {
      py: 0.125,
      px: 0.75,
      fontSize: '0.75rem',
      lineHeight: 1.2,
      minHeight: 24
    }
  };

  useEffect(() => {
    loadCustomers();
  }, [statusFilter]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        activeOnly: statusFilter === 'active' ? true : false,
        limit: 500
      };
      const response = await customerService.getAll(params);
      const data = response.data || [];
      setCustomers(data);
      setSelectedCustomer(data.length > 0 ? data[0] : null);
    } catch (err) {
      console.error('Error loading customers:', err);
      setError(err.response?.data?.message || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerClick = (customerId) => {
    navigate(`/customers/${customerId}`);
  };

  const handleEditCustomer = (customer) => {
    if (!customer?.id) return;
    navigate(`/customers/${customer.id}`);
  };

  const handleMenuOpen = (event, customer) => {
    event.stopPropagation();
    setSelectedCustomer(customer);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = (event, customer) => {
    event.stopPropagation();
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteDialogClose = () => {
    if (!deleting) {
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
    }
  };

  const confirmDeleteCustomer = async () => {
    if (!customerToDelete) return;
    try {
      setDeleting(true);
      setError('');
      await customerService.delete(customerToDelete.id);
      handleDeleteDialogClose();
      await loadCustomers();
    } catch (err) {
      console.error('Error deleting customer:', err);
      setError(err.response?.data?.message || 'Failed to delete customer');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const handlePrint = () => printWithFilename('Customers');

  return (
    <Box>
      <Box className="no-print-customers">
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Customers
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate('/customers/new')}
            sx={{ mr: 0.5 }}
          >
            New
          </Button>
          <IconButton size="small" color="primary" title="Print" onClick={(e) => setPrintMenuAnchor(e.currentTarget)}>
            <Print fontSize="small" />
          </IconButton>
          <Menu anchorEl={printMenuAnchor} open={!!printMenuAnchor} onClose={() => setPrintMenuAnchor(null)} disableEnforceFocus disableRestoreFocus>
            <MenuItem onClick={() => { handlePrint(); setPrintMenuAnchor(null); }}>Print list</MenuItem>
            <MenuItem
              onClick={() => {
                if (selectedCustomer?.id) navigate(`/customers/${selectedCustomer.id}`);
                setPrintMenuAnchor(null);
              }}
              disabled={!selectedCustomer}
            >
              Print selected
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>
          {error}
        </Alert>
      )}

      {/* Selected Customer Information Bar */}
      <Paper sx={{ p: 1.5, mb: 1.5, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer #</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedCustomer?.customer_number ?? selectedCustomer?.id ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer name</Typography>
            <Typography variant="body2">
              {selectedCustomer ? customerLongName(selectedCustomer) || '—' : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Short name</Typography>
            <Typography variant="body2">
              {selectedCustomer ? customerShortName(selectedCustomer) || '—' : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Phone</Typography>
            <Typography variant="body2">
              {selectedCustomer?.phone ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Email</Typography>
            <Typography variant="body2">
              {selectedCustomer?.email ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>City</Typography>
            <Typography variant="body2">
              {selectedCustomer?.city ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>State</Typography>
            <Typography variant="body2">
              {selectedCustomer?.state ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Country</Typography>
            <Typography variant="body2">
              {selectedCustomer?.country ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Status</Typography>
            <Chip
              label={selectedCustomer?.is_active ? 'Active' : 'Inactive'}
              color={selectedCustomer?.is_active ? 'success' : 'default'}
              size="small"
            />
          </Box>
          <IconButton
            size="small"
            onClick={() => setSelectedCustomer(null)}
            disabled={!selectedCustomer}
            sx={{ ml: 'auto' }}
            title="Clear selection"
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      {/* Search and status */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
        <Autocomplete
          freeSolo
          size="small"
          sx={{ minWidth: 280 }}
          options={customers.map((c) => ({
            id: c.id,
            label: `${c.customer_number || c.id} — ${custName(c)}`
          }))}
          getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt?.label ?? '')}
          inputValue={searchInput}
          onInputChange={(_, value) => setSearchInput(value ?? '')}
          onChange={(_, value) => {
            if (value && typeof value === 'object' && value.id) {
              const cust = customers.find((c) => c.id === value.id);
              if (cust) setSelectedCustomer(cust);
            }
          }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Search customers…" />
          )}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            displayEmpty
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Customers Table */}
      <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
        <Table size="small" stickyHeader sx={customersTableDenseSx}>
          <TableHead>
            <TableRow>
              <ResizableHeaderCell width={colWidths.custNo} onResizeStart={(e) => startResize('custNo', e)} sortDirection={sortBy === 'customer_number' ? sortOrder : false}>
                <TableSortLabel active={sortBy === 'customer_number'} direction={sortBy === 'customer_number' ? sortOrder : 'asc'} onClick={() => handleSort('customer_number')}>
                  #
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.name} onResizeStart={(e) => startResize('name', e)} sortDirection={sortBy === 'name' ? sortOrder : false}>
                <TableSortLabel active={sortBy === 'name'} direction={sortBy === 'name' ? sortOrder : 'asc'} onClick={() => handleSort('name')}>
                  Customer name
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.shortName} onResizeStart={(e) => startResize('shortName', e)} sortDirection={sortBy === 'short_name' ? sortOrder : false}>
                <TableSortLabel active={sortBy === 'short_name'} direction={sortBy === 'short_name' ? sortOrder : 'asc'} onClick={() => handleSort('short_name')}>
                  Short name
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.phone} onResizeStart={(e) => startResize('phone', e)} sortDirection={sortBy === 'phone' ? sortOrder : false}>
                <TableSortLabel active={sortBy === 'phone'} direction={sortBy === 'phone' ? sortOrder : 'asc'} onClick={() => handleSort('phone')}>
                  Phone
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.city} onResizeStart={(e) => startResize('city', e)} sortDirection={sortBy === 'city' ? sortOrder : false}>
                <TableSortLabel active={sortBy === 'city'} direction={sortBy === 'city' ? sortOrder : 'asc'} onClick={() => handleSort('city')}>
                  City
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.state} onResizeStart={(e) => startResize('state', e)} sortDirection={sortBy === 'state' ? sortOrder : false}>
                <TableSortLabel active={sortBy === 'state'} direction={sortBy === 'state' ? sortOrder : 'asc'} onClick={() => handleSort('state')}>
                  State
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.country} onResizeStart={(e) => startResize('country', e)} sortDirection={sortBy === 'country' ? sortOrder : false}>
                <TableSortLabel active={sortBy === 'country'} direction={sortBy === 'country' ? sortOrder : 'asc'} onClick={() => handleSort('country')}>
                  Country
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.salesRep} onResizeStart={(e) => startResize('salesRep', e)} sortDirection={sortBy === 'salesman_id' ? sortOrder : false}>
                <TableSortLabel active={sortBy === 'salesman_id'} direction={sortBy === 'salesman_id' ? sortOrder : 'asc'} onClick={() => handleSort('salesman_id')}>
                  Salesman
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.notes} onResizeStart={(e) => startResize('notes', e)} sortDirection={sortBy === 'notes' ? sortOrder : false}>
                <TableSortLabel active={sortBy === 'notes'} direction={sortBy === 'notes' ? sortOrder : 'asc'} onClick={() => handleSort('notes')}>
                  Comments
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.terms} onResizeStart={(e) => startResize('terms', e)} sortDirection={sortBy === 'terms' ? sortOrder : false}>
                <TableSortLabel active={sortBy === 'terms'} direction={sortBy === 'terms' ? sortOrder : 'asc'} onClick={() => handleSort('terms')}>
                  Payment Terms
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.workCode} onResizeStart={(e) => startResize('workCode', e)} sortDirection={sortBy === 'work_code' ? sortOrder : false}>
                <TableSortLabel active={sortBy === 'work_code'} direction={sortBy === 'work_code' ? sortOrder : 'asc'} onClick={() => handleSort('work_code')}>
                  Work Code
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.active} onResizeStart={(e) => startResize('active', e)} sortDirection={sortBy === 'is_active' ? sortOrder : false}>
                <TableSortLabel active={sortBy === 'is_active'} direction={sortBy === 'is_active' ? sortOrder : 'asc'} onClick={() => handleSort('is_active')}>
                  Active
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.actions} onResizeStart={(e) => startResize('actions', e)}>Actions</ResizableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredAndSortedCustomers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  {customers.length === 0 ? 'No customers found. Click "New" to create your first customer.' : 'No customers match your search.'}
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedCustomers.map((customer) => (
                <TableRow
                  key={customer.id}
                  hover
                  selected={selectedCustomer?.id === customer.id}
                  onClick={() => setSelectedCustomer(customer)}
                  onDoubleClick={() => handleCustomerClick(customer.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ width: colWidths.custNo }}>{customer.customer_number || customer.id}</TableCell>
                  <TableCell sx={{ width: colWidths.name }}>
                    {customerLongName(customer) || '—'}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.shortName }}>
                    {customerShortName(customer) || '—'}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.phone }}>{customer.phone || '-'}</TableCell>
                  <TableCell sx={{ width: colWidths.city }}>{customer.city || '-'}</TableCell>
                  <TableCell sx={{ width: colWidths.state }}>{customer.state || '-'}</TableCell>
                  <TableCell sx={{ width: colWidths.country }}>{customer.country || 'USA'}</TableCell>
                  <TableCell sx={{ width: colWidths.salesRep }}>{customer.salesman_id || customer.sales_id || '-'}</TableCell>
                  <TableCell sx={{ width: colWidths.notes }}>
                    {customer.notes
                      ? customer.notes.length > 30 
                        ? customer.notes.substring(0, 30) + '...'
                        : customer.notes
                      : '-'}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.terms }}>{customer.terms || '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.workCode }}>{customer.work_code || '-'}</TableCell>
                  <TableCell sx={{ width: colWidths.active }}>
                    <Chip
                      label={customer.is_active ? 'Y' : 'N'}
                      color={customer.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ width: colWidths.actions }} onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, customer)}
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      </Box>

      {/* Print view – customer list */}
      <PrintPage title="Customers" showSpacer={false}>
        <p style={{ fontSize: 12, marginBottom: 8 }}>
          {filteredAndSortedCustomers.length} record(s). Printed{' '}
          {new Date().toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' })}
        </p>
        <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ padding: 6, textAlign: 'left' }}>Customer #</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Customer name</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Short name</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Phone</th>
              <th style={{ padding: 6, textAlign: 'left' }}>City</th>
              <th style={{ padding: 6, textAlign: 'left' }}>State</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Active</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedCustomers.map((c) => (
              <tr key={c.id}>
                <td style={{ padding: 6 }}>{c.customer_number || c.id}</td>
                <td style={{ padding: 6 }}>{customerLongName(c) || '—'}</td>
                <td style={{ padding: 6 }}>{customerShortName(c) || '—'}</td>
                <td style={{ padding: 6 }}>{c.phone || '-'}</td>
                <td style={{ padding: 6 }}>{c.city || '-'}</td>
                <td style={{ padding: 6 }}>{c.state || '-'}</td>
                <td style={{ padding: 6 }}>{c.is_active ? 'Y' : 'N'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </PrintPage>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedCustomer) handleCustomerClick(selectedCustomer.id);
          handleMenuClose();
        }}>
          View Details
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedCustomer) handleEditCustomer(selectedCustomer);
          handleMenuClose();
        }}        >
          Edit
        </MenuItem>
        <MenuItem onClick={() => { handlePrint(); handleMenuClose(); }}>Print</MenuItem>
        <MenuItem onClick={(e) => selectedCustomer && handleDeleteClick(e, selectedCustomer)} sx={{ color: 'error.main' }}>
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Customer Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose} aria-labelledby="delete-customer-dialog-title" aria-describedby="delete-customer-dialog-description">
        <DialogTitle id="delete-customer-dialog-title">Delete Customer?</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-customer-dialog-description">
            Are you sure you want to delete {customerToDelete ? custName(customerToDelete) : 'this customer'}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} disabled={deleting}>Cancel</Button>
          <Button onClick={confirmDeleteCustomer} color="error" variant="contained" autoFocus disabled={deleting}>{deleting ? 'Deleting…' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Customers;
